import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { submitInquiry } from "../lib/api";

export default function QuoteDialog({ product, open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await submitInquiry({
        ...form,
        product_interest: product?.name,
        source: "quote",
      });
      toast.success("Quote request received. We'll be in touch within 24 hours.");
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
      onOpenChange(false);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="quote-dialog"
        className="max-w-lg bg-brand-surface border-brand-line rounded-sm"
      >
        <DialogHeader>
          <p className="eyebrow">Request a Quote</p>
          <DialogTitle className="font-serif text-3xl text-brand-ink">
            {product?.name || "Product Inquiry"}
          </DialogTitle>
          <DialogDescription className="text-brand-muted">
            Share your requirements and our sourcing team will reach out with
            pricing, availability, COAs and documentation.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4 mt-2" data-testid="quote-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="q-name" className="text-xs tracking-wider uppercase">Full name*</Label>
              <Input
                id="q-name"
                value={form.name}
                onChange={update("name")}
                data-testid="quote-input-name"
                className="mt-1.5 bg-white border-brand-line rounded-sm py-6"
              />
            </div>
            <div>
              <Label htmlFor="q-email" className="text-xs tracking-wider uppercase">Work email*</Label>
              <Input
                id="q-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                data-testid="quote-input-email"
                className="mt-1.5 bg-white border-brand-line rounded-sm py-6"
              />
            </div>
            <div>
              <Label htmlFor="q-phone" className="text-xs tracking-wider uppercase">Phone</Label>
              <Input
                id="q-phone"
                value={form.phone}
                onChange={update("phone")}
                data-testid="quote-input-phone"
                className="mt-1.5 bg-white border-brand-line rounded-sm py-6"
              />
            </div>
            <div>
              <Label htmlFor="q-company" className="text-xs tracking-wider uppercase">Company</Label>
              <Input
                id="q-company"
                value={form.company}
                onChange={update("company")}
                data-testid="quote-input-company"
                className="mt-1.5 bg-white border-brand-line rounded-sm py-6"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="q-message" className="text-xs tracking-wider uppercase">Quantity & requirements*</Label>
            <Textarea
              id="q-message"
              value={form.message}
              onChange={update("message")}
              data-testid="quote-input-message"
              placeholder="e.g. 500 kg per quarter, need COA and MSDS"
              className="mt-1.5 bg-white border-brand-line rounded-sm min-h-[120px]"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full disabled:opacity-60"
            data-testid="quote-submit-btn"
          >
            {loading ? "Sending…" : "Send Request"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
